/**
 * AVT Smart Toilets Product Reference Sheet
 * 
 * This file serves as the master reference for all products, mapping:
 * - AVT Product Names to Factory Model Numbers
 * - Product Images and Assets
 * - Internal Codes for Logistics/Shipping
 * - Product Categories and Descriptions
 */

// Import all product images
import luxaSeriesProductMain from "@/assets/luxa-series-product-main.jpg";
import luxaSeriesProduct from "@/assets/luxa-series-product.jpg";
import luxaSeriesProductOpen from "@/assets/luxa-series-product-open.jpg";
import luxaSeriesProductSide from "@/assets/luxa-series-product-side.jpg";

import lunaSeriesProductMain from "@/assets/luna-series-product-main.jpg";
import lunaSeriesProduct from "@/assets/luna-series-product.jpg";
import lunaSeriesProductAngle from "@/assets/luna-series-product-angle.jpg";
import lunaSeriesProductDetail from "@/assets/luna-series-product-detail.jpg";
import lunaSeriesProductSide from "@/assets/luna-series-product-side.jpg";

import novaSeriesProductMain from "@/assets/nova-series-product-main.jpg";
import novaSeriesProduct from "@/assets/nova-series-product.jpg";
import novaSeriesProductAngle from "@/assets/nova-series-product-angle.jpg";
import novaSeriesProductSeat from "@/assets/nova-series-product-seat.jpg";
import novaSeriesLifestyle from "@/assets/nova-series-lifestyle.jpg";

import ariaSeriesProductMain from "@/assets/aria-series-product-main.jpg";
import ariaSeriesProduct from "@/assets/aria-series-product.jpg";
import ariaSeriesOpenSeat from "@/assets/aria-series-open-seat.jpg";
import ariaSeriesLifestyleLuxury from "@/assets/aria-series-lifestyle-luxury.jpg";
import ariaSeriesLifestyleModern from "@/assets/aria-series-lifestyle-modern.jpg";
import ariaSeriesInstallation from "@/assets/aria-series-installation.jpg";

import lumaSeriesProduct from "@/assets/luma-series-product.jpg";
import lumaSeriesProductSide from "@/assets/luma-series-product-side.jpg";
import lumaSeriesProductOpen from "@/assets/luma-series-product-open.jpg";
import lumaSeriesLifestyle from "@/assets/luma-series-lifestyle.jpg";

import aureluxSeriesProductMain from "@/assets/aurelux-series-product-main.jpg";
import aureluxSeriesLifestyle from "@/assets/aurelux-series-lifestyle.jpg";
import aureluxSeriesProductAngle from "@/assets/aurelux-series-product-angle.jpg";
import aureluxSeriesControls from "@/assets/aurelux-series-controls.jpg";

import aureliusSeriesProductMain from "@/assets/aurelius-series-product-main.jpg";
import aureliusSeriesProductAngle from "@/assets/aurelius-series-product-angle.jpg";
import aureluxSeriesProductSide from "@/assets/aurelux-series-product-side.jpg";
import aureluxSeriesProductDetail from "@/assets/aurelux-series-product-detail.jpg";
import aureliusSeriesLifestyle from "@/assets/aurelius-series-lifestyle.jpg";

import venaSeriesImage from "@/assets/vena-series-product.jpg";
import u1ProductMain from "@/assets/u1-product-main.jpg";
import u1FeaturesGrid from "@/assets/u1-features-grid.jpg";
import u1ColorOptions from "@/assets/u1-color-options.jpg";
import u1DesignDark from "@/assets/u1-design-dark.jpg";
import u1AmbientLight from "@/assets/u1-ambient-light.jpg";

// K2 Quadrate Series Images
import k2HeroDesert from "@/assets/k2-hero-desert.jpg";
import k2LifestyleLuxury from "@/assets/k2-lifestyle-luxury.jpg";
import k2ModernizeSquare from "@/assets/k2-modernize-square.jpg";
import k2ColorOptions from "@/assets/k2-color-options.jpg";
import k2SeatRingComfort from "@/assets/k2-seat-ring-comfort.jpg";

// S1 Super Egg Series Images
import s1HeroEgg from "@/assets/s1-hero-egg.jpg";
import s1EggShapeAward from "@/assets/s1-egg-shape-award.jpg";
import s1AutoFunctions from "@/assets/s1-auto-functions.jpg";
import s1SeatHeating from "@/assets/s1-seat-heating.jpg";

// M3 Smart Toilet Series Images
import m3HeroMain from "@/assets/m3-hero-main.jpg";
import m3MultipleFunctions from "@/assets/m3-multiple-functions.jpg";
import m3RadarSensor from "@/assets/m3-radar-sensor.jpg";
import m3SeatHeating from "@/assets/m3-seat-heating.jpg";

// L1 Smart Bidet Toilet Images
import l1ProductMain from "@/assets/l1-product-main.png";
import l1AngleView from "@/assets/l1-angle-view.png";
import l1Features from "@/assets/l1-features.png";
import l1Installation from "@/assets/l1-installation.png";
import l1SideView from "@/assets/l1-side-view.png";

export interface ProductReference {
  // AVT Marketing Information
  avtName: string;           // Customer-facing name (e.g., "LUXA")
  avtSeries: string;         // Product series (e.g., "LUXA Series")
  
  // Internal Reference Codes
  internalCode: string;      // Your internal SKU/reference
  factoryModel: string;      // Original factory model number
  logisticsCode: string;     // Code for shipping/inventory
  
  // Product Classification
  category: 'flagship' | 'wellness-focused' | 'tech-forward' | 'health-tech' | 'next-gen' | 'luxury' | 'square-design' | 'egg-design';
  priceRange: 'premium' | 'luxury' | 'ultra-luxury';
  
  // Asset References
  images: {
    main: string;            // Primary product image
    gallery: string[];       // Additional product images
    lifestyle?: string[];    // Lifestyle/installation images
  };
  
  // Product Information
  tagline: string;
  description: string;
  keyFeatures: string[];
  
  // Technical Specifications
  specifications: {
    dimensions: string;
    weight: string;
    powerConsumption: string;
    waterUsage: string;
    warranty: string;
    installation: string;
  };
  
  // Routing Information
  urlSlug: string;          // URL path (e.g., "/products/luxa")
  pageComponent?: string;   // React component name if exists
}

export const PRODUCT_REFERENCE: Record<string, ProductReference> = {
  // LUXA Series - Premium Smart Toilet
  LUXA: {
    avtName: "LUXA",
    avtSeries: "LUXA Series", 
    internalCode: "AVT-LUX-001",
    factoryModel: "M80",
    logisticsCode: "SHIP-LUX-ST",
    category: "luxury",
    priceRange: "luxury",
    images: {
      main: luxaSeriesProductMain,
      gallery: [
        luxaSeriesProduct,
        luxaSeriesProductOpen, 
        luxaSeriesProductSide
      ]
    },
    tagline: "Luxury Redefined",
    description: "Experience unparalleled luxury with the LUXA Series smart toilet, featuring premium materials and intelligent automation.",
    keyFeatures: [
      "Premium ceramic construction",
      "Heated seat with memory settings",
      "Advanced bidet functionality", 
      "Automatic lid operation",
      "LED ambient lighting",
      "Self-cleaning nozzles"
    ],
    specifications: {
      dimensions: "680mm × 390mm × 790mm",
      weight: "65kg",
      powerConsumption: "1400W",
      waterUsage: "4.8L/3.2L dual flush",
      warranty: "5 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/luxa",
    pageComponent: "Luxa"
  },

  // LUNA Series - Wellness-Focused Smart Toilet
  LUNA: {
    avtName: "LUNA",
    avtSeries: "LUNA Series",
    internalCode: "AVT-LUN-002", 
    factoryModel: "Factory-LUN-2024",
    logisticsCode: "SHIP-LUN-ST",
    category: "wellness-focused",
    priceRange: "premium",
    images: {
      main: lunaSeriesProductMain,
      gallery: [
        lunaSeriesProduct,
        lunaSeriesProductAngle,
        lunaSeriesProductDetail,
        lunaSeriesProductSide
      ]
    },
    tagline: "Wellness Reimagined", 
    description: "The LUNA Series focuses on health and wellness with spa-like features and therapeutic functions.",
    keyFeatures: [
      "Therapeutic warm water system",
      "Massage functionality",
      "Air purification system",
      "Health monitoring sensors",
      "Aromatherapy integration",
      "Stress-relief programs"
    ],
    specifications: {
      dimensions: "660mm × 385mm × 780mm",
      weight: "62kg", 
      powerConsumption: "1200W",
      waterUsage: "4.5L/3L dual flush",
      warranty: "5 Years Comprehensive", 
      installation: "Professional Required"
    },
    urlSlug: "/products/luna",
    pageComponent: "Luna"
  },

  // NOVA Series - Tech-Forward Smart Toilet
  NOVA: {
    avtName: "NOVA",
    avtSeries: "NOVA Series",
    internalCode: "AVT-NOV-003",
    factoryModel: "TEG5", 
    logisticsCode: "SHIP-NOV-ST",
    category: "tech-forward",
    priceRange: "premium",
    images: {
      main: novaSeriesProductMain,
      gallery: [
        novaSeriesProduct,
        novaSeriesProductAngle,
        novaSeriesProductSeat
      ],
      lifestyle: [novaSeriesLifestyle]
    },
    tagline: "Innovation Unleashed",
    description: "The NOVA Series represents cutting-edge technology with AI-powered features and smart connectivity.",
    keyFeatures: [
      "AI-powered user recognition",
      "Voice control integration", 
      "Smart app connectivity",
      "Predictive maintenance",
      "Energy optimization",
      "Customizable user profiles"
    ],
    specifications: {
      dimensions: "670mm × 395mm × 785mm", 
      weight: "68kg",
      powerConsumption: "1300W",
      waterUsage: "4.2L/2.8L dual flush",
      warranty: "5 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/nova",
    pageComponent: "Nova"
  },

  // ARIA Series - Architectural Elegance
  ARIA: {
    avtName: "ARIA", 
    avtSeries: "ARIA Series",
    internalCode: "AVT-ARI-004",
    factoryModel: "H60",
    logisticsCode: "SHIP-ARI-ST", 
    category: "luxury",
    priceRange: "ultra-luxury",
    images: {
      main: ariaSeriesProductMain,
      gallery: [
        ariaSeriesProduct,
        ariaSeriesOpenSeat
      ],
      lifestyle: [
        ariaSeriesLifestyleLuxury,
        ariaSeriesLifestyleModern, 
        ariaSeriesInstallation
      ]
    },
    tagline: "Architectural Masterpiece",
    description: "The ARIA Series combines architectural elegance with intelligent automation for the ultimate luxury experience.",
    keyFeatures: [
      "Architectural design aesthetics",
      "Premium material finish",
      "Silent operation technology",
      "Luxury bidet functions",
      "Ambient mood lighting", 
      "Gesture control features"
    ],
    specifications: {
      dimensions: "690mm × 400mm × 800mm",
      weight: "72kg",
      powerConsumption: "1500W", 
      waterUsage: "4.0L/2.5L dual flush",
      warranty: "7 Years Premium",
      installation: "Professional + Design Consultation"
    },
    urlSlug: "/products/aria"
  },


  // LUMA Series - Illuminated Design
  LUMA: {
    avtName: "LUMA", 
    avtSeries: "LUMA Series",
    internalCode: "AVT-LUM-010",
    factoryModel: "Factory-LUM-2024",
    logisticsCode: "SHIP-LUM-ST",
    category: "tech-forward",
    priceRange: "luxury",
    images: {
      main: lumaSeriesProduct,
      gallery: [
        lumaSeriesProduct,
        lumaSeriesProductSide,
        lumaSeriesProductOpen
      ],
      lifestyle: [lumaSeriesLifestyle]
    },
    tagline: "Illuminated Innovation",
    description: "The LUMA Series combines intelligent lighting with advanced smart toilet technology.",
    keyFeatures: [
      "Dynamic LED lighting",
      "Smart ambiance control", 
      "Mood-responsive illumination",
      "Advanced bidet system",
      "Touch-free operation",
      "Customizable light patterns"
    ],
    specifications: {
      dimensions: "668mm × 388mm × 782mm",
      weight: "64kg",
      powerConsumption: "1280W", 
      waterUsage: "4.4L/3.0L dual flush",
      warranty: "5 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/luma"
  },


  // AURELUX Series - Gold Standard
  AURELUX: {
    avtName: "AURELUX",
    avtSeries: "AURELUX Series",
    internalCode: "AVT-AUR-012", 
    factoryModel: "Factory-AUR-2024",
    logisticsCode: "SHIP-AUR-ST", 
    category: "flagship",
    priceRange: "ultra-luxury",
    images: {
      main: aureluxSeriesProductMain,
      gallery: [
        aureluxSeriesProductMain,
        aureluxSeriesProductAngle,
        aureluxSeriesControls
      ],
      lifestyle: [aureluxSeriesLifestyle]
    },
    tagline: "The Gold Standard",
    description: "The AURELUX Series represents the absolute pinnacle of luxury with gold-standard features and materials.", 
    keyFeatures: [
      "Luxury material construction",
      "24k gold accents",
      "Bespoke user experience",
      "Concierge-level service",
      "Handcrafted details",
      "Exclusive technology suite"
    ],
    specifications: {
      dimensions: "710mm × 410mm × 820mm",
      weight: "85kg", 
      powerConsumption: "1800W",
      waterUsage: "3.5L/2.0L dual flush",
      warranty: "10 Years Platinum",
      installation: "White Glove Service"
    },
    urlSlug: "/products/aurelux"
  },

  // AURELIUS Series - Wider Comfort Smart Toilet  
  AURELIUS: {
    avtName: "AURELIUS",
    avtSeries: "AURELIUS Series", 
    internalCode: "AVT-AURELIUS-013",
    factoryModel: "M2",
    logisticsCode: "SHIP-AURELIUS-ST",
    category: "luxury",
    priceRange: "luxury",
    images: {
      main: aureliusSeriesProductMain,
      gallery: [
        aureliusSeriesProductMain,
        aureliusSeriesProductAngle,
        aureluxSeriesProductSide,
        aureluxSeriesProductDetail
      ],
      lifestyle: [aureliusSeriesLifestyle]
    },
    tagline: "Wider Comfort, Premium Design",
    description: "The AURELIUS Series features wider seat comfort with sleek premium appearance and intelligent user memory features for personalized luxury.",
    keyFeatures: [
      "Wider comfort seat design for enhanced user experience",
      "Dual voltage compatibility (110V/60Hz & 220V/50Hz)",
      "User memory settings for personalized experience",
      "Instant water heating technology",
      "Multi-pattern bidet spray functions",
      "Automatic lid and seat operation",
      "LED night light with motion sensor",
      "Heated seat with temperature control",
      "Self-cleaning nozzle system",
      "Wireless remote control",
      "Energy-saving eco mode",
      "Soft-close seat and lid",
      "Premium ceramic construction",
      "Advanced hygiene features"
    ],
    specifications: {
      dimensions: "710mm × 410mm × 450mm",
      weight: "42kg",
      powerConsumption: "1100W",
      waterUsage: "4.0L/2.5L dual flush",
      warranty: "3 Years Comprehensive",
      installation: "Professional Recommended"
    },
    urlSlug: "/products/aurelius",
    pageComponent: "Aurelius"
  },

  // VENA Series - Eco-Luxury Smart Toilet
  VENA: {
    avtName: "VENA",
    avtSeries: "VENA Series",
    internalCode: "AVT-VEN-014",
    factoryModel: "M80F",
    logisticsCode: "SHIP-VEN-ST",
    category: "flagship",
    priceRange: "luxury",
    images: {
      main: venaSeriesImage,
      gallery: [venaSeriesImage]
    },
    tagline: "Eco-Luxury Innovation",
    description: "The VENA Series combines sustainable luxury with advanced smart toilet technology, featuring water-saving systems and eco-conscious design.",
    keyFeatures: [
      "Eco-friendly water-saving technology",
      "Motion sensor automation",
      "LED night illumination",
      "Adjustable temperature control",
      "Antibacterial self-cleaning",
      "Dual-flush system",
      "Energy-efficient operation",
      "Sustainable luxury design"
    ],
    specifications: {
      dimensions: "680mm × 395mm × 785mm",
      weight: "63kg",
      powerConsumption: "1250W",
      waterUsage: "3.8L/2.3L dual flush",
      warranty: "3 Years Premium",
      installation: "Professional Required"
    },
    urlSlug: "/products/vena",
    pageComponent: "Vena"
  },

  // U1 Overflow Series - Advanced Dual Flush Smart Toilet
  U1: {
    avtName: "U1",
    avtSeries: "U1 Overflow Series",
    internalCode: "AVT-U1-015",
    factoryModel: "U1",
    logisticsCode: "SHIP-U1-ST",
    category: "tech-forward",
    priceRange: "premium",
    images: {
      main: u1ProductMain,
      gallery: [
        u1ProductMain,
        u1FeaturesGrid,
        u1ColorOptions,
        u1DesignDark
      ],
      lifestyle: [u1AmbientLight]
    },
    tagline: "Overflow Protection Redefined",
    description: "The U1 Overflow Series features advanced dual flush technology with anti-backflow protection, stunning ambient lighting, and premium matte finish options.",
    keyFeatures: [
      "Dual flush technology (3L/4.5L)",
      "Anti-backflow protection system",
      "Ambient LED lighting",
      "Premium matte finish options",
      "Boosting pump system",
      "Water pressure optimization",
      "Silent operation",
      "Energy-efficient design"
    ],
    specifications: {
      dimensions: "670mm × 390mm × 780mm",
      weight: "58kg",
      powerConsumption: "1150W",
      waterUsage: "3L/4.5L dual flush",
      warranty: "3 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/u1",
    pageComponent: "U1"
  },

  // K2 Quadrate Series - Revolutionary Square Design Smart Toilet
  K2: {
    avtName: "K2",
    avtSeries: "K2 Quadrate Series",
    internalCode: "AVT-K2-016",
    factoryModel: "K2",
    logisticsCode: "SHIP-K2-ST",
    category: "square-design",
    priceRange: "premium",
    images: {
      main: k2HeroDesert,
      gallery: [
        k2HeroDesert,
        k2LifestyleLuxury,
        k2ModernizeSquare,
        k2ColorOptions
      ],
      lifestyle: [k2SeatRingComfort]
    },
    tagline: "Square Design • New Aesthetics",
    description: "The K2 Quadrate Series features a revolutionary square toilet design with innovative aesthetics, instant heating, massage wash, and space-saving design. Available in White and Silver Grey.",
    keyFeatures: [
      "Revolutionary square toilet design",
      "Square water tank - no pressure limit",
      "Square seat ring for comfort",
      "Instant tankless water heating",
      "Massage wash bidet function",
      "Warm air drying system",
      "40mm shorter - saves space",
      "Available in White and Silver Grey"
    ],
    specifications: {
      dimensions: "660mm × 400mm × 520mm",
      weight: "55kg",
      powerConsumption: "1300W",
      waterUsage: "4L/6L dual flush",
      warranty: "3 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/k2",
    pageComponent: "K2"
  },

  // S1 Super Egg Series - Award-Winning Egg-Shaped Smart Toilet
  S1: {
    avtName: "S1",
    avtSeries: "S1 Super Egg Series",
    internalCode: "AVT-S1-017",
    factoryModel: "S1",
    logisticsCode: "SHIP-S1-ST",
    category: "egg-design",
    priceRange: "luxury",
    images: {
      main: s1HeroEgg,
      gallery: [
        s1HeroEgg,
        s1EggShapeAward,
        s1AutoFunctions,
        s1SeatHeating
      ],
      lifestyle: [s1EggShapeAward]
    },
    tagline: "Award-Winning Egg Design • Your First Choice",
    description: "The S1 Super Egg features a patent egg-shaped spherical design, 2022 MUSE Platinum Award winner, with no water pressure limit, powerful booster pump, auto functions, and 4-level seat heating.",
    keyFeatures: [
      "Patent egg-shaped spherical design",
      "2022 MUSE Platinum Award Winner",
      "No water pressure limit",
      "Built-in intelligent water tank",
      "Powerful booster pump system",
      "Auto open/close & flush",
      "4-level seat heating (34°C-40°C)",
      "Various wash modes for family"
    ],
    specifications: {
      dimensions: "Spherical Design",
      weight: "58kg",
      powerConsumption: "1400W",
      waterUsage: "4L/6L dual flush",
      warranty: "3 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/s1",
    pageComponent: "S1"
  },

  // M3 Smart Toilet - Voice Control & Radar Sensor
  M3: {
    avtName: "M3",
    avtSeries: "M3 Smart Toilet Series",
    internalCode: "AVT-M3-018",
    factoryModel: "M3",
    logisticsCode: "SHIP-M3-ST",
    category: "tech-forward",
    priceRange: "premium",
    images: {
      main: m3HeroMain,
      gallery: [
        m3HeroMain,
        m3MultipleFunctions,
        m3RadarSensor,
        m3SeatHeating
      ],
      lifestyle: [m3MultipleFunctions]
    },
    tagline: "Voice Control & Radar Sensor Technology",
    description: "The M3 Smart Toilet features controller and voice control, seat heating at 36°C, radar sensor auto open/close, warm air drying, smart cleaning bidet, no water pressure limit, and one-piece elongated design.",
    keyFeatures: [
      "Controller + voice control",
      "Radar sensor auto open/close",
      "No water pressure limit",
      "Smart cleaning bidet system",
      "Thermostatic seat heating 36°C",
      "Multi-level warm air drying",
      "Foot kick seat ring control",
      "Wider U-shaped ergonomic seat"
    ],
    specifications: {
      dimensions: "680mm × 380mm × 520mm",
      weight: "55kg",
      powerConsumption: "1300W",
      waterUsage: "4L/6L dual flush",
      warranty: "3 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/m3",
    pageComponent: "M3"
  },

  // L1 ZENO Series - Compact Smart Bidet Toilet
  L1: {
    avtName: "L1",
    avtSeries: "ZENO L1 Series",
    internalCode: "AVT-L1-019",
    factoryModel: "L1",
    logisticsCode: "SHIP-L1-ST",
    category: "tech-forward",
    priceRange: "premium",
    images: {
      main: l1ProductMain,
      gallery: [
        l1ProductMain,
        l1AngleView,
        l1Features,
        l1SideView
      ],
      lifestyle: [l1Installation]
    },
    tagline: "Compact Intelligence • 26 Premium Features",
    description: "The ZENO L1 Smart Bidet Toilet combines 26 advanced features in a compact design, featuring instant heating, UV sterilization, foam shield technology, and intelligent automation for modern bathrooms.",
    keyFeatures: [
      "Compact one-piece design",
      "Instant tankless water heating",
      "UV LED sterilization system",
      "Foam shield technology",
      "Auto open/close lid",
      "Heated seat with 4-level control",
      "Warm air dryer system",
      "LED ambient night light"
    ],
    specifications: {
      dimensions: "650mm × 380mm × 520mm",
      weight: "48kg",
      powerConsumption: "1200W",
      waterUsage: "4L/6L dual flush",
      warranty: "3 Years Comprehensive",
      installation: "Professional Required"
    },
    urlSlug: "/products/l1",
    pageComponent: "L1"
  }
};

/**
 * Helper functions for product reference management
 */

// Get product by AVT name
export const getProductByName = (avtName: string): ProductReference | undefined => {
  return PRODUCT_REFERENCE[avtName.toUpperCase()];
};

// Get product by internal code
export const getProductByInternalCode = (internalCode: string): ProductReference | undefined => {
  return Object.values(PRODUCT_REFERENCE).find(product => product.internalCode === internalCode);
};

// Get product by factory model
export const getProductByFactoryModel = (factoryModel: string): ProductReference | undefined => {
  return Object.values(PRODUCT_REFERENCE).find(product => product.factoryModel === factoryModel);
};

// Get product by URL slug
export const getProductBySlug = (slug: string): ProductReference | undefined => {
  return Object.values(PRODUCT_REFERENCE).find(product => product.urlSlug === slug);
};

// Get all products by category
export const getProductsByCategory = (category: ProductReference['category']): ProductReference[] => {
  return Object.values(PRODUCT_REFERENCE).filter(product => product.category === category);
};

// Get all products by price range
export const getProductsByPriceRange = (priceRange: ProductReference['priceRange']): ProductReference[] => {
  return Object.values(PRODUCT_REFERENCE).filter(product => product.priceRange === priceRange);
};

// Export all AVT names for easy reference
export const AVT_PRODUCT_NAMES = Object.keys(PRODUCT_REFERENCE);

// Export categories for filtering
export const PRODUCT_CATEGORIES = ['flagship', 'wellness-focused', 'tech-forward', 'health-tech', 'next-gen', 'luxury', 'square-design', 'egg-design'] as const;

// Export price ranges
export const PRICE_RANGES = ['premium', 'luxury', 'ultra-luxury'] as const;